import { useState } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import { Button } from 'react-bootstrap';
import { User } from 'redux/schemas/models/my-account';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { quarterSpacing, halfSpacing, largeSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { yellow, success, highTide, gray2 } from 'styles/global_defaults/colors';
import ProfilePicture from 'org_level_profile/components/profile-picture';
import ParticipantPill from 'athena/components/participant-pill';
import prodPathReplace from 'shared/prod-path-rewrite';
import { fadeInAnimation } from './styles';

const MAX_RESPONSE_LENGTH = 450;

const baseCardStyles = css`
  ${fadeInAnimation};
  background: rgba(255, 255, 255, 0.8);
  gap: ${halfSpacing}px;
`;

const contentStyles = (isMentor: boolean) => css`
  gap: ${threeQuartersSpacing}px;
  text-align: ${isMentor ? 'start' : 'end'};
`;

const attributionStyles = (isMentor: boolean) => css`
  gap: ${halfSpacing}px;
  ${!isMentor ? 'justify-content: end;' : ''}
`;

const decorativeIconStyles = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

type ReflectionResponseCardProps = {
  questionText: string;
  response: string;
  user: User;
  role: MentoringProgramRole;
  index: number;
};

const cardStyles = (isMentor: boolean) => css`
  ${baseCardStyles};
  ${isMentor
    ? `border-left: ${quarterSpacing}px solid ${yellow};
       border-radius: 0 ${standardSpacing}px ${standardSpacing}px ${standardSpacing}px;
       padding: ${largeSpacing}px 150px ${largeSpacing}px ${largeSpacing}px;`
    : `border-right: ${quarterSpacing}px solid ${success};
       border-radius: ${standardSpacing}px 0 ${standardSpacing}px ${standardSpacing}px;
       padding: ${largeSpacing}px ${largeSpacing}px ${largeSpacing}px 130px;`
  }
`;

const readMoreButtonStyles = (isMentor: boolean) => css`
  background: none;
  border: none;
  padding: 0;
  align-self: ${isMentor ? 'flex-start' : 'flex-end'};
  cursor: pointer;

  color: ${gray2} !important;
  
  &:hover {
    color: ${highTide} !important;
  }
`;

const ReflectionResponseCard = ({ questionText, response, user, role, index }: ReflectionResponseCardProps) => {
  const translationsBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REFLECTION_WORKSPACE.SHARED_REFLECTIONS;
  const isMentor = role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR;
  const iconSrc = prodPathReplace(`images/reflection-${isMentor ? 'mentor' : 'mentee'}-${index + 1}.svg`);
  const iconSize = !isMentor && index === 1 ? 127 : 115;

  const isTruncatable = response.length > MAX_RESPONSE_LENGTH;
  const [isExpanded, setIsExpanded] = useState(false);
  const displayedResponse = isTruncatable && !isExpanded
    ? `${response.slice(0, MAX_RESPONSE_LENGTH)}…`
    : response;

  return (
    <div css={cardStyles(isMentor)} className='d-flex flex-column position-relative overflow-hidden'>
      <img
        css={[decorativeIconStyles, css`${isMentor ? 'right' : 'left'}: ${largeSpacing}px; width: ${iconSize}px;`]}
        src={iconSrc}
        alt=''
        aria-hidden='true'
      />
      <div css={contentStyles(isMentor)} className='d-flex flex-column'>
        <p className='text-large-body semi-bold mb-0'>{questionText}</p>
        <p className='text-large-body font-italic mb-0'>{displayedResponse}</p>
      </div>
      {isTruncatable && !isExpanded && (
        <Button
          variant='link'
          css={readMoreButtonStyles(isMentor)}
          className='text-medium semi-bold'
          onClick={() => setIsExpanded(true)}
        >
          {translationsBase.READ_MORE()}
        </Button>
      )}
      <div css={attributionStyles(isMentor)} className='d-flex align-items-center'>
        {isMentor ? (
          <>
            <ProfilePicture profile={user} size={30} />
            <span className='text-small gray-2'>– {user.firstName} {user.lastName}</span>
            <ParticipantPill role={role} />
          </>
        ) : (
          <>
            <ParticipantPill role={role} />
            <span className='text-small gray-2'>– {user.firstName} {user.lastName}</span>
            <ProfilePicture profile={user} size={30} />
          </>
        )}
      </div>
    </div>
  );
};

export default ReflectionResponseCard;
